/* Purrfolk facets: filtering, sorting and pagination through the Section Rendering API.
   Depends on purr-core.js (window.Purr: toast, announce, reduceMotion). Loaded with `defer` by sections/main-collection.liquid.
   Markup contract (see main-collection.liquid):
     .purr-collection[data-section-id]     root that gets swapped
     form[data-facet-form="chips|sheet"] one desktop form, one bottom-sheet form (same inputs)
     [data-facet-sort]                   <select> (outside the forms) – synced into input[name=sort_by][data-facet-sort-value]
     [data-facet-sort-option]            buttons in the mobile sort menu
     [data-facet-price]                  number inputs (debounced 500 ms)
     [data-facet-remove] [data-facet-clear] .purr-pagination a   links intercepted and fetched
     purr-drawer[data-facet-drawer]        kept alive across swaps (only its body/footer are replaced)
     [data-facet-loading]                skeleton grid, shown when a request takes longer than 150 ms
     [data-density]                      grid density buttons (localStorage "purr_grid_cols"); the root carries
                                         data-density-toggle="false" when the merchant disabled the toggle
     [data-load-more]                    "load more" link (pagination_type = load_more): next page is fetched and its cards appended */
(() => {
  'use strict';
  if (window.__purrFacetsBound) return;
  window.__purrFacetsBound = true;

  const Purr = window.Purr || (window.Purr = {});
  const ROOT = '.purr-collection[data-section-id]';
  const STORAGE_KEY = 'purr_grid_cols';
  const SKELETON_DELAY = 150;
  const PRICE_DEBOUNCE = 500;

  let controller = null;
  let lastUrl = location.pathname + location.search;
  let priceTimer = null;
  let pendingForm = null;

  /* ---------- Helpers ---------- */
  const getRoot = (el) => (el && el.closest && el.closest(ROOT)) || document.querySelector(ROOT);
  const reduceMotion = () => (typeof Purr.reduceMotion === 'function' ? Purr.reduceMotion() : window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const modifiedClick = (e) => e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

  /* The allowed column values are whatever the section rendered as density buttons (the merchant's configured
     column count is always among them), so a stored preference can never fall outside the merchant's set. */
  const allowedCols = (root) => Array.from(root.querySelectorAll('[data-density]')).map((b) => b.getAttribute('data-density'));
  const densityEnabled = (root) => root.getAttribute('data-density-toggle') !== 'false' && root.querySelector('[data-density]') !== null;
  const readCols = (allowed) => { try { const v = localStorage.getItem(STORAGE_KEY); return allowed.includes(v) ? v : null; } catch (err) { return null; } };
  const writeCols = (v) => { try { localStorage.setItem(STORAGE_KEY, v); } catch (err) { /* storage unavailable */ } };

  function applyDensity(root) {
    if (!root) return;
    if (!densityEnabled(root)) return; // toggle disabled: the merchant's setting always wins
    const stored = readCols(allowedCols(root));
    if (stored) root.querySelectorAll('[data-product-grid], [data-facet-loading] .purr-product-grid').forEach((g) => g.setAttribute('data-cols', stored));
    const grid = root.querySelector('[data-product-grid]');
    const current = grid ? grid.getAttribute('data-cols') : stored;
    root.querySelectorAll('[data-density]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-density') === current)));
  }

  /* ---------- Load more (append next page) ---------- */
  async function loadMore(link, root) {
    const grid = root.querySelector('[data-product-grid]');
    if (!grid || link.getAttribute('aria-busy') === 'true') return;
    const sectionId = root.getAttribute('data-section-id');
    const target = new URL(link.href, location.origin);
    target.hash = '';
    target.searchParams.set('section_id', sectionId);
    const label = link.querySelector('[data-label]') || link;
    const idleText = label.textContent;
    link.setAttribute('aria-busy', 'true');
    if (link.dataset.loadingText) label.textContent = link.dataset.loadingText;
    try {
      const res = await fetch(target.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = doc.querySelector('[data-product-grid]');
      if (!nextGrid) throw new Error('Section markup missing');
      const items = Array.from(nextGrid.children);
      items.forEach((item) => grid.appendChild(item));
      const nextLink = doc.querySelector('[data-load-more]');
      link.removeAttribute('aria-busy');
      label.textContent = idleText;
      if (nextLink && nextLink.getAttribute('href')) link.setAttribute('href', nextLink.getAttribute('href'));
      else (link.closest('[data-load-more-wrap]') || link).remove();
      const first = items[0] && items[0].querySelector('a[href]');
      if (first) first.focus({ preventScroll: true });
      if (typeof Purr.initReveal === 'function') Purr.initReveal();
      if (typeof Purr.announce === 'function' && link.dataset.loadedText) Purr.announce(link.dataset.loadedText.replace('[[count]]', String(items.length)));
      Purr.emit && Purr.emit('purr:collection:more', { root, count: items.length });
    } catch (err) {
      link.removeAttribute('aria-busy');
      label.textContent = idleText;
      if (typeof Purr.toast === 'function') Purr.toast((Purr.strings && Purr.strings.genericError) || 'Something went wrong. Please try again.', { type: 'error' });
    }
  }

  /* Build the target URL from a facet form (or from the current location when no form exists). */
  function buildUrl(form, overrides = {}) {
    const params = new URLSearchParams();
    let base = location.pathname;
    if (form) {
      base = form.getAttribute('action') || base;
      const fd = new FormData(form);
      fd.forEach((value, key) => { if (typeof value === 'string' && value.trim() !== '') params.append(key, value.trim()); });
    } else {
      new URLSearchParams(location.search).forEach((value, key) => { if (value !== '') params.append(key, value); });
    }
    params.delete('page');
    params.delete('section_id');
    if (overrides.sort != null) params.set('sort_by', overrides.sort);
    const url = new URL(base, location.origin);
    url.search = params.toString();
    return url;
  }

  function syncSort(root, value) {
    root.querySelectorAll('input[name="sort_by"][data-facet-sort-value]').forEach((i) => { i.value = value; });
    root.querySelectorAll('[data-facet-sort]').forEach((s) => { if (s.value !== value) s.value = value; });
  }

  function findControl(root, formType, name, value, typed) {
    const form = root.querySelector('form[data-facet-form="' + formType + '"]');
    if (!form) return null;
    let el = null;
    for (const c of form.elements) {
      if (c.name === name && (value == null || c.value === value)) { el = c; break; }
    }
    if (el && typed != null && el.value !== typed && parseFloat(el.value) === parseFloat(typed)) el.value = typed;
    return el;
  }

  /* ---------- DOM swap ---------- */
  function swap(root, next) {
    const drawer = root.querySelector('purr-drawer[data-facet-drawer]');
    const nextDrawer = next.querySelector('purr-drawer[data-facet-drawer]');
    const openDisclosure = root.querySelector('purr-disclosure[open][data-facet-disclosure]');
    const openKey = openDisclosure ? openDisclosure.getAttribute('data-facet-disclosure') : null;

    // Keep the sheet element alive (open state, focus trap, scroll lock) – only refresh its content.
    if (drawer && nextDrawer) {
      ['.purr-drawer__body', '.purr-drawer__footer'].forEach((sel) => {
        const a = drawer.querySelector(sel), b = nextDrawer.querySelector(sel);
        if (a && b) a.innerHTML = b.innerHTML;
      });
      nextDrawer.remove();
    }

    Array.from(root.children).forEach((child) => { if (child !== drawer) child.remove(); });
    const frag = document.createDocumentFragment();
    Array.from(next.children).forEach((child) => frag.appendChild(child));
    if (drawer) root.insertBefore(frag, drawer); else root.appendChild(frag);

    if (drawer && drawer.hasAttribute('open')) {
      root.querySelectorAll('[data-drawer-open="' + drawer.id + '"]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    }
    applyDensity(root);
    if (openKey) {
      const again = root.querySelector('purr-disclosure[data-facet-disclosure="' + openKey + '"]');
      if (again && typeof again.open === 'function') again.open();
    }
  }

  /* ---------- Fetch + render ---------- */
  async function load(url, opts = {}) {
    const root = opts.root || document.querySelector(ROOT);
    if (!root) return;
    const sectionId = root.getAttribute('data-section-id');
    const target = new URL(url, location.origin);
    target.hash = '';
    target.searchParams.delete('section_id');
    const fetchUrl = new URL(target.toString());
    fetchUrl.searchParams.set('section_id', sectionId);

    if (controller) controller.abort();
    controller = new AbortController();
    const mine = controller;

    const drawer = root.querySelector('purr-drawer[data-facet-drawer]');
    const loading = root.querySelector('[data-facet-loading]');
    root.setAttribute('aria-busy', 'true');
    if (drawer) drawer.setAttribute('aria-busy', 'true');
    const skeletonTimer = setTimeout(() => {
      if (mine.signal.aborted) return;
      if (loading) { loading.hidden = false; loading.removeAttribute('aria-hidden'); }
      root.classList.add('is-loading');
    }, SKELETON_DELAY);

    const previousUrl = lastUrl;
    const nextUrl = target.pathname + target.search;
    if (opts.push !== false) {
      history.pushState({ facets: true }, '', nextUrl);
      lastUrl = nextUrl;
    }

    const finish = () => {
      clearTimeout(skeletonTimer);
      root.classList.remove('is-loading');
      root.removeAttribute('aria-busy');
      if (drawer) drawer.removeAttribute('aria-busy');
      if (loading) { loading.hidden = true; loading.setAttribute('aria-hidden', 'true'); }
    };

    try {
      const res = await fetch(fetchUrl.toString(), { signal: mine.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
      const html = await res.text();
      if (mine.signal.aborted) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector(ROOT);
      if (!next) throw new Error('Section markup missing');

      clearTimeout(skeletonTimer);
      swap(root, next);
      root.classList.remove('is-loading');
      root.removeAttribute('aria-busy');
      if (drawer) drawer.removeAttribute('aria-busy');
      lastUrl = nextUrl;
      pendingForm = null;

      const count = root.querySelector('[data-product-count]');
      const countText = count ? count.textContent.trim() : '';
      document.querySelectorAll('[data-collection-count]').forEach((el) => { if (!root.contains(el) && countText) el.textContent = countText; });

      if (opts.scroll) root.scrollIntoView({ block: 'start', behavior: reduceMotion() ? 'auto' : 'smooth' });

      let focusTarget = null;
      if (typeof opts.focus === 'function') focusTarget = opts.focus(root);
      if (!focusTarget && count) { count.setAttribute('tabindex', '-1'); focusTarget = count; }
      if (focusTarget) focusTarget.focus({ preventScroll: true });
      if (countText && typeof Purr.announce === 'function') Purr.announce(countText);
      if (typeof Purr.initReveal === 'function') Purr.initReveal();
      Purr.emit && Purr.emit('purr:facets:updated', { root, url: nextUrl });
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      finish();
      if (opts.push !== false) { history.replaceState({ facets: true }, '', previousUrl); lastUrl = previousUrl; }
      if (typeof opts.revert === 'function') opts.revert();
      if (typeof Purr.toast === 'function') Purr.toast((Purr.strings && Purr.strings.genericError) || 'Something went wrong. Please try again.', { type: 'error' });
    }
  }

  /* ---------- Change handling ---------- */
  function handleFormChange(form, input) {
    const root = getRoot(form);
    if (!root) return;
    const formType = form.getAttribute('data-facet-form') || 'chips';
    const name = input.name;
    const isCheck = input.type === 'checkbox' || input.type === 'radio';
    const value = isCheck ? input.value : null;
    const typed = input.type === 'number' ? input.value : null;
    const wasChecked = isCheck ? input.checked : null;
    load(buildUrl(form), {
      root,
      focus: (r) => findControl(r, formType, name, value, typed),
      revert: () => { if (isCheck && document.contains(input)) input.checked = !wasChecked; }
    });
  }

  function handleSort(root, value, focus) {
    syncSort(root, value);
    const form = root.querySelector('form[data-facet-form]');
    load(buildUrl(form, { sort: value }), { root, focus });
  }

  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!(t instanceof HTMLElement)) return;
    if (t.matches('[data-facet-sort]')) {
      const root = getRoot(t);
      if (root) handleSort(root, t.value, (r) => r.querySelector('[data-facet-sort]'));
      return;
    }
    if (t.matches('[data-facet-price]')) return; // handled by the debounced input listener
    const form = t.closest('form[data-facet-form]');
    if (form && (t.matches('input, select'))) handleFormChange(form, t);
  });

  document.addEventListener('input', (e) => {
    const t = e.target;
    if (!(t instanceof HTMLElement) || !t.matches('[data-facet-price]')) return;
    const form = t.closest('form[data-facet-form]');
    if (!form) return;
    clearTimeout(priceTimer);
    pendingForm = form;
    priceTimer = setTimeout(() => { pendingForm = null; handleFormChange(form, t); }, PRICE_DEBOUNCE);
  });

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[data-facet-form]')) return;
    e.preventDefault();
    const root = getRoot(form);
    const drawer = form.closest('purr-drawer');
    if (pendingForm === form) {
      clearTimeout(priceTimer);
      pendingForm = null;
      load(buildUrl(form), { root });
    }
    if (drawer && typeof drawer.close === 'function') {
      drawer.close();
      const count = root && root.querySelector('[data-product-count]');
      if (count) { count.setAttribute('tabindex', '-1'); count.focus({ preventScroll: true }); }
    }
  });

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;

    const density = t.closest('[data-density]');
    if (density) {
      const root = getRoot(density);
      writeCols(density.getAttribute('data-density'));
      applyDensity(root);
      return;
    }

    const sortOption = t.closest('[data-facet-sort-option]');
    if (sortOption) {
      const root = getRoot(sortOption);
      const disclosure = sortOption.closest('purr-disclosure');
      if (disclosure && typeof disclosure.close === 'function') disclosure.close();
      if (root) handleSort(root, sortOption.getAttribute('data-facet-sort-option'));
      return;
    }

    const opener = t.closest('.purr-collection [data-drawer-open]');
    if (opener) {
      const d = document.getElementById(opener.getAttribute('data-drawer-open'));
      if (d && typeof d.open === 'function') { e.preventDefault(); d.open(); }
      return;
    }

    const link = t.closest('a[href]');
    if (!link || modifiedClick(e)) return;
    const root = getRoot(link);
    if (!root) return;
    if (link.matches('[data-load-more]')) {
      if (link.origin !== location.origin) return;
      e.preventDefault();
      loadMore(link, root);
      return;
    }
    const isRemove = link.matches('[data-facet-remove]');
    const isClear = link.matches('[data-facet-clear]');
    const isPage = !!link.closest('.purr-pagination');
    if (!isRemove && !isClear && !isPage) return;
    if (link.origin !== location.origin) return;
    e.preventDefault();
    const inSheet = !!link.closest('purr-drawer[data-facet-drawer]');
    load(link.href, {
      root,
      scroll: isPage,
      focus: inSheet ? (r) => r.querySelector('purr-drawer[data-facet-drawer] form[data-facet-form] input:not([type="hidden"]):not([disabled])') : null
    });
  });

  window.addEventListener('popstate', () => {
    const current = location.pathname + location.search;
    if (current === lastUrl) return;
    const root = document.querySelector(ROOT);
    if (!root) return;
    load(location.href, { root, push: false });
  });

  /* ---------- Init ---------- */
  const init = () => {
    const root = document.querySelector(ROOT);
    if (!root) return;
    applyDensity(root);
    try { history.replaceState(Object.assign({}, history.state || {}, { facets: true }), '', location.href); } catch (err) { /* ignore */ }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', (e) => { const root = e.target && e.target.querySelector ? e.target.querySelector(ROOT) : null; if (root) applyDensity(root); });
})();
